import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, Filter, Plus, RefreshCw, Search, Settings2,
} from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";

/**
 * Documents list.
 *
 * The status of a document matters more than anything else on this screen. A
 * draft is a working note; an issued document has a number and is somebody's
 * accounting record; a voided one still holds its number. Those three must be
 * unmistakable at a glance, which is why status leads each row rather than
 * sitting as a subtle tag.
 */

const STATUS = {
  draft: { label: "Draft", tone: "bg-muted text-muted-foreground" },
  issued: { label: "Issued", tone: "bg-emerald-500/10 text-emerald-600" },
  voided: { label: "Voided", tone: "bg-destructive/10 text-destructive" },
};

const TYPE_SHORT = {
  self_billed_invoice: "Self-billed invoice",
  purchase_order: "Purchase order",
  payment_statement: "Payment statement",
};

export default function DocumentsPage() {
  const navigate = useNavigate();
  const { authAxios, isAdmin, isSubAdmin } = useAuth();
  const canConfigure = isAdmin || isSubAdmin;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get("/documents");
      setDocs(res.data);
    } catch {
      toast.error("Could not load documents");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const visible = docs.filter((d) => {
    if (typeFilter !== "all" && d.doc_type !== typeFilter) return false;
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [d.number, d.vendor_snapshot?.name, d.vendor_snapshot?.legal_name, d.title]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Documents</h1>
          <p className="text-sm text-muted-foreground">
            Invoices, purchase orders and payment statements
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canConfigure && (
            <Button variant="outline" size="sm" className="gap-1.5"
                    onClick={() => navigate("/documents/settings")}
                    data-testid="docs-settings">
              <Settings2 className="h-4 w-4" aria-hidden="true" />
              Billing details
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh documents"
                  data-testid="docs-refresh">
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </Button>
          <Button size="sm" className="gap-1.5" onClick={() => navigate("/documents/new")}
                  data-testid="docs-new">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New document
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-hidden="true" />
          <Input className="h-11 pl-9" placeholder="Search number or vendor"
                 aria-label="Search documents" value={query}
                 onChange={(e) => setQuery(e.target.value)} data-testid="docs-search" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Filters">
        {[["all", "All types"], ...Object.entries(TYPE_SHORT)].map(([key, label]) => (
          <Button key={key} size="sm" className="h-10"
                  variant={typeFilter === key ? "default" : "outline"}
                  onClick={() => setTypeFilter(key)}
                  data-testid={`filter-type-${key}`}>
            {label}
          </Button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2" role="group" aria-label="Status filters">
        {[["all", "All"], ["draft", "Drafts"], ["issued", "Issued"], ["voided", "Voided"]]
          .map(([key, label]) => (
            <Button key={key} size="sm" className="h-10"
                    variant={statusFilter === key ? "secondary" : "ghost"}
                    onClick={() => setStatusFilter(key)}
                    data-testid={`filter-status-${key}`}>
              {label}
            </Button>
          ))}
      </div>

      {visible.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center space-y-3" data-testid="docs-empty">
            <FileText className="h-9 w-9 mx-auto text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {docs.length === 0
                ? "No documents yet. Create one from a vendor's orders, or from scratch."
                : "Nothing matches those filters."}
            </p>
            {docs.length === 0 && (
              <Button className="h-11 gap-1.5" onClick={() => navigate("/documents/new")}
                      data-testid="docs-empty-new">
                <Plus className="h-4 w-4" aria-hidden="true" />
                New document
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((doc) => {
            const s = STATUS[doc.status] || STATUS.draft;
            return (
              <Card key={doc.document_id}
                    className="hover:border-primary/40 transition-colors"
                    data-testid={`doc-${doc.document_id}`}>
                <CardContent className="p-0">
                  <button type="button"
                          className="w-full text-left p-4 flex flex-wrap items-center justify-between gap-3"
                          onClick={() => navigate(`/documents/${doc.document_id}`)}
                          data-testid={`open-${doc.document_id}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5
                                          text-xs font-medium ${s.tone}`}>
                          {s.label}
                        </span>
                        <span className="font-mono text-sm font-semibold text-foreground">
                          {doc.number || "— not issued"}
                        </span>
                      </div>
                      <p className="text-sm text-foreground mt-1 truncate">
                        {doc.vendor_snapshot?.legal_name || doc.vendor_snapshot?.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {doc.title}
                        {doc.issued_at ? ` · issued ${doc.issued_at.slice(0, 10)}` : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-foreground">${doc.total}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.lines?.length || 0} line{doc.lines?.length === 1 ? "" : "s"}
                      </p>
                    </div>
                  </button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
